import moment from "moment";
import React from "react";
import { BatteryMedium, Clock3, History, Signal, Waves } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getSensorDepths } from "../utils/sensorDepths";

const toPercentage = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, numericValue));
};

export default function DBSensor({ filteredSensors, sensorDepths }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const signal = toPercentage(filteredSensors.signal);
  const charge = toPercentage(filteredSensors.charge);
  const depths = getSensorDepths(sensorDepths);
  const moistureLevels = [
    { depth: `${depths.mv1} cm`, value: filteredSensors.humidityMV1 },
    { depth: `${depths.mv2} cm`, value: filteredSensors.humidityMV2 },
    { depth: `${depths.mv3} cm`, value: filteredSensors.humidityMV3 },
  ];
  const lastReading = filteredSensors.time
    ? moment(filteredSensors.time)
        .locale(i18n.language === "en" ? "en-gb" : i18n.language)
        .format("L, LT")
    : "—";

  return (
    <section className="sensor-reading-card">
      <div className="sensor-reading-header">
        <div>
          <span className="sensor-detail-section-kicker">
            {t("sensor_code")}
          </span>
          <h2>{filteredSensors.code}</h2>
        </div>
        <div className="sensor-reading-health">
          <span>
            <Signal size={15} aria-hidden="true" />
            {Math.round(signal)}%
          </span>
          <span>
            <BatteryMedium size={16} aria-hidden="true" />
            {Math.round(charge)}%
          </span>
        </div>
      </div>

      <div className="sensor-reading-content">
        <div className="sensor-moisture-heading">
          <span className="sensor-reading-water-icon">
            <Waves size={20} aria-hidden="true" />
          </span>
          <div>
            <h3>{t("sensor_stats_soil")}</h3>
            <p>{t("overview")} · {depths.mv1}–{depths.mv3} cm</p>
          </div>
        </div>

        <div className="sensor-moisture-levels">
          {moistureLevels.map((level) => {
            const percentage = toPercentage(level.value);
            return (
              <div className="sensor-moisture-row" key={level.depth}>
                <span className="sensor-moisture-depth">{level.depth}</span>
                <div className="sensor-moisture-track">
                  <div className="sensor-moisture-gradient" />
                  <span
                    className="sensor-moisture-marker"
                    style={{ "--sensor-position": `${percentage}%` }}
                    aria-label={`${level.depth}: ${percentage}%`}
                  >
                    <span>{Math.round(percentage)}%</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="sensor-moisture-legend" aria-hidden="true">
          <span>{t("Critical")}</span>
          <span>{t("Optimal")}</span>
          <span>{t("Full")}</span>
        </div>
      </div>

      <footer className="sensor-reading-footer">
        <div>
          <Clock3 size={16} aria-hidden="true" />
          <span>{t("last_reading")}</span>
          <strong>{lastReading}</strong>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/my-history/${filteredSensors.code}`)}
        >
          <History size={16} aria-hidden="true" />
          {t("history")}
        </button>
      </footer>
    </section>
  );
}
